/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
/* eslint-disable no-invalid-this */
import {AnimationAwaiter, defineCustomElement} from 'extensions/ui-utils';
import {createFocusTrap} from 'focus-trap';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {keyed} from 'lit/directives/keyed.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import styles from './g-modal.scss?as=stylesheet';

/**
 * The Genesis `Modal` webcomponent. Extends `LitElement`.
 * @see https://lit.dev/docs/components/overview/
 * @class
 * @extends {LitElement}
 */
export default class GenesisModal extends LitElement {

	/**
	 * Define customized shadow root options.
	 * @see https://lit.dev/docs/components/shadow-dom/#setting-shadowrootoptions
	 * @static
	 */
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true
	};

	/**
	 * Define stylesheets
	 * @see https://lit.dev/docs/components/styles/
	 * @static
	 */
	static get styles () {
		return [styles];
	}

	/**
	 * Define reflector properties
	 * @see https://lit.dev/docs/components/properties/
	 * @static
	 */
	static get properties () {
		return {
			_content: {
				state: true
			},
			_title: {
				state: true
			},
			align: {
				type: String
			},
			animation: {
				type: String
			},
			contentPadding: {
				attribute: 'content-padding',
				type: String
			},
			direction: {
				type: String
			},
			header: {
				type: String
			},
			isOpen: {
				attribute: 'is-open',
				type: Boolean
			},
			labeledBy: {
				attribute: 'labeled-by',
				type: String
			},
			lock: {
				type: String
			},
			overlay: {
				type: String
			},
			small: {
				type: String
			},
			target: {
				reflect: true,
				type: String
			},
			type: {
				type: String
			}
		};
	}

	/**
	 * Used to determine the loaded state.
	 * @private
	 * @type {String}
	 */
	#loadedType = null;

	/**
	 * @private
	 * @type {import('focus-trap').FocusTrap}
	 */
	#focusTrap = null;

	/**
	 * Getter used to determine which id is represented in the "aria-labeledby" attribute.
	 * @private
	 */
	get #labeledBy () {
		return this.labeledBy || 'title';
	}

	/**
	 * The list of valid animations
	 * @type {String[]}
	 */
	animations = [
		'fade',
		'slide'
	];

	/**
	 * @constructor
	 */
	constructor () {
		super();
		this._content = '';
		this._title = '';

		/**
		 * @type {'center'|'top'|'bottom'}
		 */
		this.align = 'center';
		this.animation = 'fade';
		this.contentPadding = 'true';
		this.direction = 'center';
		this.header = 'true';
		this.isOpen = false;
		this.labeledBy = '';
		this.lock = 'true';
		this.overlay = 'true';
		this.small = 'false';
		this.type = 'basic';
		this.target = '';
	}

	getExternalTriggers () {
		return document.querySelectorAll(`[data-g-modal-trigger="${this.id}"]`);
	}

	connectedCallback () {
		super.connectedCallback();

		// Find external triggers and bind events
		const externalTriggers = this.getExternalTriggers();
		for (let externalTrigger of externalTriggers) {
			externalTrigger.addEventListener('click', this.open.bind(this));
		}
	}

	disconnectedCallback () {
		// Find external triggers and unbind events
		const externalTriggers = this.getExternalTriggers();
		for (let externalTrigger of externalTriggers) {
			externalTrigger.addEventListener('click', this.open.bind(this));
		}

		super.disconnectedCallback();
	}

	/**
	 * Helper method to dynamically create a focus trap on the ShadowDom and slotted children nodes.
	 * @see https://github.com/focus-trap/focus-trap
	 * @returns {import('focus-trap').FocusTrap} A FocusTrap instance.
	 */
	createFocusTrap () {
		return createFocusTrap(this, {
			allowOutsideClick: true,
			checkCanFocusTrap: () => this.updateComplete,
			escapeDeactivates: false,
			tabbableOptions: {
				getShadowRoot: () => true
			}
		});
	}

	/**
	 * Logic to be ran before the {@link GenesisModal.open \.open()} method is fired.
	 * @returns {Promise<void>} A resolved promise used for the purpose of `async await` downstream.
	 */
	async beforeOpen () {
		if (this.#loadedType !== this.type) {
			// Run logic depending on which type is defined
			switch (this.type) {
				case 'ajax': {
					const response = await fetch(this.target);
					const text = await response.text();
					const virtualDom = new DOMParser().parseFromString(text, 'text/html');

					// Define reactive properties
					this._content = virtualDom.body.innerHTML;
					this._title = virtualDom.title;
					break;
				}
				default:
					break;
			}

			// Set loaded type
			this.#loadedType = this.type;
		}

		// Return dummy promise
		return Promise.resolve();
	}

	/**
	 * Event handler used for the `slotchange` event for the trigger slot.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onTriggerSlotChange = (e) => {
		const triggerElement = e.target.assignedNodes({flatten: true})
			.find(node => node.nodeName === 'A' || node.nodeName === 'BUTTON');

		// Assign the open event to the anchor or button element inside the trigger slot
		if (triggerElement) {
			triggerElement.addEventListener('click', this.open.bind(this), false);
		}
	};

	/**
	 * Event handler used for the `keyup` event for on the Document. Used to close the modal when pressing the Escape key.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onDocumentKeyup = (e) => {
		if (e.key === 'Escape') {
			this.close();
		}
	};

	/**
	 * Event handler used for the `click` event for on the Document. Used to close the modal when clicking outside of the modal.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onDocumentClick = (e) => {
		// eslint-disable-next-line no-extra-parens
		const path = e.path || (e.composedPath && e.composedPath()) || [];
		const contains = path?.find(element => element === this);

		if (!contains) {
			this.close();
		}
	};

	/**
	 * Method used programmatically open the modal window.
	 * @async
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	async open (e) {
		e?.preventDefault();

		// Wait for any before open logic to run
		await this.beforeOpen();

		// Set the modal property isOpen to true (this should open the modal)
		this.isOpen = true;

		// Lock body overflow if `lock` is true
		if (this.lock && this.lock === 'true') {
			Object.assign(document.body.style, {
				overflow: 'hidden'
			});
		}

		// Wait for animations to finish
		await new AnimationAwaiter('#modal', {
			container: this.renderRoot,
			hasAnimation: () => this.animation && this.animations.indexOf(this.animation) > -1
		}).animationEnd;

		// Bind event to the document
		document.addEventListener('keyup', this.onDocumentKeyup, false);
		document.addEventListener('click', this.onDocumentClick, false);

		// Activate the focus trap
		this.#focusTrap = this.createFocusTrap();
		this.#focusTrap.activate();
	}

	/**
	 * Method used programmatically close the modal window.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	close = (e) => {
		e?.preventDefault();

		// Set the modal property isOpen to false (this should close the modal)
		this.isOpen = false;

		// Remove document event handlers
		document.removeEventListener('keyup', this.onDocumentKeyup, false);
		document.removeEventListener('click', this.onDocumentClick, false);

		// Unlock body overflow if `lock` is true
		if (this.lock && this.lock === 'true') {
			Object.assign(document.body.style, {
				overflow: ''
			});
		}

		// Deactivate the trap
		this.#focusTrap.deactivate();
	};

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	render () {

		/**
		 * Create classMap object to cleanly output the dynamic classes on the #modal div
		 * @type {import('lit/directives/class-map').ClassInfo}
		 */
		const modalDynamicClasses = {
			'g-modal': true,
			'has-header': this.header && this.header === 'true',
			'has-padding': this.contentPadding === 'true',
			'is-open': this.isOpen,
			'is-small': this.small && this.small === 'true',
			[`type--${this.type}`]: true,
			[`animation--${this.animation}`]: true,
			[`direction--${this.direction}`]: true,
			[`align--${this.align}`]: true
		};

		// Return the html template
		return html`
			<slot name="trigger" @slotchange="${this.onTriggerSlotChange}"></slot>
			<div
				role="dialog"
				aria-modal="true"
				aria-hidden="${this.isOpen ? 'false' : 'true'}"
				aria-labelledby="${this.#labeledBy}"
				aria-describedby="content"
				id="modal"
				class="${classMap(modalDynamicClasses)}"
			>
				${
					this.overlay && this.overlay === 'true'
						? html`<div class="g-modal__overlay" @click="${this.close}" tabindex="-1"></div>`
						: nothing
				}
				<div class="g-modal__container" aria-modal="true" id="container">
					${
						this.header && this.header === 'true'
							? html`<header class="g-modal__header">
								<div class="g-modal__title" id="title">
									${this._title
										? unsafeHTML(this._title)
										: html`<slot name="title" @slotchange="${this.onTitleSlotChange}"></slot>`}
								</div>
								<button type="button" @click="${this.close}" class="g-modal__close" aria-label="Close Modal"></button>
							</header>`
							: nothing
					}
					<main class="g-modal__content" id="content">
						${
							this.header && this.header === 'false'
								? html`<button type="button" @click="${this.close}" class="g-modal__close" aria-label="Close Modal"></button>`
								: nothing
						}
						${
							this.type === 'iframe' && this.#loadedType
								? keyed(this.isOpen, html`<iframe src="${this.target}">Loading...</iframe>`)
								: this._content
									? unsafeHTML(this._content)
									: html`<slot name="content"></slot>`
						}
					</main>
				</div>
			</div>
		`;
	}
};

defineCustomElement('g-modal', GenesisModal);
